import React, { memo, useState, useRef, useEffect, useMemo } from 'react';
import './DropdownMenu.css';
import { Trans, useTranslation } from 'react-i18next';
import Tooltip from './Tooltip';

interface IDropdownMenuProps {
	inputId: string;
	items: IDDItem[];
	selectedItemId: string;
	onSelect: (selectedItemId: string) => void;
	className?: string;
	disabled?: boolean;
	tooltip?: React.ReactNode;
}

export interface IDDItem {
	id: string;
	translationKey: string;
}

const DropdownMenu: React.FC<IDropdownMenuProps> = ({
	items,
	onSelect,
	selectedItemId,
	inputId,
	className,
	disabled = false,
	tooltip
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<string>(selectedItemId);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const openable = useMemo(() => {
		return items && items.length > 1;
	}, items);

	useEffect(() => {
		setSelectedItem(selectedItemId);
	}, [selectedItemId]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSelect = (itemId: string) => {
		onSelect(itemId);

		setSelectedItem(itemId);
		setIsOpen(false);
	};

	const { i18n } = useTranslation();

	return (
		<div className={`dropdown-container ${isOpen ? 'open' : ''} ${className}`} ref={dropdownRef}>
			<Tooltip content={tooltip}>
				<div
					className={`dropdown-button ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
					data-testid={`dropdown-button-${inputId}`}
					onClick={() => !disabled && openable && setIsOpen(!isOpen)}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
				>
					<span className="dropdown-placeholder">{(() => {
						const item = items.find(i => i.id === selectedItem);
						return item ? <Trans
							key={i18n.language}
							i18nKey={item.translationKey}
							components={{
								sub: <sub />,
								sup: <sup />,
							}}
						/> : null;
					})()}</span>
					{openable && <span className="dropdown-arrow">›</span>}
				</div>
			</Tooltip>

			{isOpen && (
				<ul className="dropdown-list" role="listbox">
					{items.map((item, index) => (
						<li
							key={index}
							data-testid={`unit-option-${item.id}`}
							className={`dropdown-item${selectedItem === item.id ? ' selected' : ''}`}
							onClick={() => !disabled && handleSelect(item.id)}
							role="option"
							aria-selected={selectedItem === item.id}
						>
							{
								<Trans
									key={i18n.language}
									i18nKey={item.translationKey}
									components={{
										sub: <sub />,
										sup: <sup />,
									}}
								/>
							}
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default memo(DropdownMenu);